
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { QuickeditService } from '../../../services/quickedit.service';
import { AbstractDetailsComponent } from 'app/components/shared/abstract-details.component';
import { Observable, defer, Subject } from 'rxjs';

@Component({
	selector: 'audiotrack-quickedit',
	templateUrl: './audiotrack-quickedit.component.html',
	styleUrls: ['./audiotrack-quickedit.component.scss']
})
export class AudiotrackQuickEditComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	@Input() row: any;
	@Input() languages: any[] = [];

	@Output() saved: EventEmitter<any> = new EventEmitter<any>();
	@Output() removed: EventEmitter<any> = new EventEmitter<any>();
	@Output() canceled: EventEmitter<any> = new EventEmitter<any>();
	@Output() edited: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('formRef', { static: true }) form: NgForm;

	languageOptions: any[] = [];
	automatedName: string;

	label: string;
	saveAction: Observable<void>;

	isDeleted: boolean;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected quickeditService: QuickeditService) {
		super(route, location);

		this.entityConfiguration = {
			name: 'audiotrack',
			entityTranslation: this.translate.instant('AUDIOTRACK'),
			standAlone: true,
			parentPath: '/library/mediaclips',
			basePath: '/library/mediaclip/'
		};
	}

	ngOnInit() {
		this.label = this.entityConfiguration.name.toUpperCase();
		this.automatedName = null;
		if (this.row && this.row.data) {
			this.entity = this.row.data;
			if (this.row.data.automatedName) {
				this.automatedName = this.row.data.automatedName;
			}
		}
		super.ngOnInit();
		if (this.languages) {
			this.languageOptions = this.languages.map((language: any) => ({
				value: language.isocode,
				label: this.translate.instant('LANGUAGE_' + language.isocode.toUpperCase())
			}));
		}

		this.saveAction = defer(() => this.save(true));
		this.quickeditService.addSaveAction(this.saveAction);
	}

	ngOnDestroy() {
		if (!this.isDeleted) {
			this.resetEntity();
		}
		if (this.saveAction) {
			this.quickeditService.removeSaveAction(this.saveAction);
		}
	}

	saveQuickEdit() {
		this.save(true).subscribe(() => {
			this.quickeditService.removeSaveAction(this.saveAction);
		});
	}

	save(force: boolean): Observable<void> {
		delete this.row.data.automatedName;

		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe((response) => {
			if (this.isNew) {
				this.row.id = this.entity.id;
				this.row.data = this.entity;
				this.row.quickEditing = false;

				if (
					(typeof this.row.data.name === 'undefined' || this.row.data.name === '')
					&& this.row && this.row.data && this.row.data.languageid && this.languages && this.languages.length > 0
				) {
					// get language name based on id
					let languageName;
					for (let i = 0, l = this.languages.length; i < l; i++) {
						if (parseFloat(this.languages[i].id) === parseFloat(this.row.data.languageid)) {
							languageName = this.languages[i].name;
						}
					}

					if (languageName) {
						this.row.data.name = languageName;
						this.row.data.languagename = languageName;
					}
				}

				setTimeout(() => {
					this.saved.emit(true);
				}, 10);
			}
			// this.resetEntity();
		});
		return saveSubject;
	}

	delete(): Subject<void> {
		const deleteSubject = super.delete();
		this.isDeleted = true;

		deleteSubject.subscribe((response) => {
			this.removed.emit();
		});
		return deleteSubject;
	}

	resetEntity() {
		this.cancel().subscribe((entity) => {
			const audiotrack = entity as any;
			if (this.automatedName) {
				audiotrack.automatedName = this.automatedName;
			}
			this.row.data = entity;
			this.entity = entity;
			if (audiotrack && audiotrack.isocode) {
				this.onIsocodeChange(audiotrack.isocode);
			}
		});
	}

	onCancelClick() {
		if (this.isNew) {
			this.canceled.emit(true);
			this.quickeditService.removeSaveAction(this.saveAction);
		} else {
			this.resetEntity();
			this.row.quickEditing = false;
		}
	}

	onIsocodeChange(isocode: string) {
		const lang = this.languages.find((language: any) => language.isocode === isocode);

		if (lang && lang.id) {
			this.row.data.languageid = lang.id;
			this.row.data.languagename = lang.name;
		}
	}
}
